import { DateTime, Duration } from "luxon";
import { Accessibility, Dimensions, OccupancyBooking } from "./booking";
import * as React from "react";

export function formatCurrency(amount: number, currency: string, language: string) {
    return new Intl.NumberFormat(language, {
        style: 'currency',
        currency
    }).format(amount);
}

export function formatBookingPeriod(booking: OccupancyBooking) {
    return (
        <span>
            <strong>{DateTime.fromISO(booking.from).toLocaleString(DateTime.DATE_SHORT)}</strong>
            {" - "}
            <strong>{DateTime.fromISO(booking.until).toLocaleString(DateTime.DATE_SHORT)}</strong>
        </span>
    );
}

interface LocalTime {
    hour: number;
    minute: number;
    second: number;
}

export function parseTime(time?: string): LocalTime | undefined {
    if (time) {
        const parts = time.split(":");
        try {
            return {
                hour: Number.parseInt(parts[0]),
                minute: Number.parseInt(parts[1]) || 0,
                second: Number.parseInt(parts[3]) || 0,
            };
        }
        catch (error) {
            console.error("Could not parse time", time);
            return undefined;
        }
    }
}

/**
 * Compares two LocalTime values, chekcs only hours, minutes and seconds.
 * 
 * @param t1 
 * @param t2 
 * @returns negative when t1 before t2, 0 when equal
 */
export function compareTime(t1: LocalTime, t2: LocalTime) {
    if (t1.hour != t2.hour) {
        return t1.hour - t2.hour;
    }
    if (t1.minute != t2.minute) {
        return t1.minute - t2.minute;
    }
    return t1.second - t2.second;
}

/**
 * Find the matching accessibility entry for the datetimeOfBooking.
 * In case the customer books short after midnight the function returns the accessibility for the day before.
 */
export function findAccessibility(weekdayAccessibility: Accessibility, accessibleFrom: string | undefined, accessibleUntil: string | undefined, datetimeOfBooking: DateTime) {
    let dayOfWeek = weekday(datetimeOfBooking);

    let dayOfWeekBefore = weekday(datetimeOfBooking.plus(Duration.fromObject({ days: - 1 })))
    if (weekdayAccessibility[dayOfWeekBefore] && weekdayAccessibility[dayOfWeekBefore].until) {
        var fromDayBefore = parseTime(weekdayAccessibility[dayOfWeekBefore].from);
        var untilDayBefore = parseTime(weekdayAccessibility[dayOfWeekBefore].until);
        var isTimeDefintionOverMidnight = compareTime(fromDayBefore as LocalTime, untilDayBefore as LocalTime) > 0;
        if (isTimeDefintionOverMidnight) { 
            if (compareTime(datetimeOfBooking, untilDayBefore as LocalTime) < 0) {
                dayOfWeek = dayOfWeekBefore;
            }
        }
    }

    return {
        from: findWeekdayAccessibility(weekdayAccessibility, "from", dayOfWeek) || accessibleFrom,
        until: findWeekdayAccessibility(weekdayAccessibility, "until", dayOfWeek) || accessibleUntil,
    }
}

export type Weekdays = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

function weekday(datetime: DateTime): 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday' {
    let weekday: Weekdays;
    switch (datetime.get('weekday')) {
        case 1: weekday = 'Monday'; break;
        case 2: weekday = 'Tuesday'; break;
        case 3: weekday = 'Wednesday'; break;
        case 4: weekday = 'Thursday'; break;
        case 5: weekday = 'Friday'; break;
        case 6: weekday = 'Saturday'; break;
        default: weekday = 'Sunday'; break;
    }
    return weekday;
}

function findWeekdayAccessibility(weekdayAccessibility: Accessibility, property: "from" | "until", weekday: Weekdays) {
    return weekday && weekdayAccessibility[weekday] && weekdayAccessibility[weekday][property];
}

export function getVolume(dimensions?: Dimensions) {
    if (dimensions) {
        const { width, height, depth } = dimensions;
        if (width && height && depth) {
            return width * height * depth;
        }
    }
}

export function calculateBookingEnd(bookingUnit: "Days" | "Hours", bookingStart: DateTime, units = 1,
    accessibility: Accessibility,
    accessibleFrom: string | undefined,
    accessibleUntil: string | undefined,
    now: DateTime = DateTime.local()) {
    if (bookingUnit == "Days") {
        let bookingEnd = bookingStart.plus(Duration.fromObject({ days: units - 1 }))

        const { from, until } = findAccessibility(accessibility, accessibleFrom, accessibleUntil, bookingEnd);
        const fromTime = parseTime(from) ?? { hour: 0, minute: 0, second: 0 };
        const untilTime = parseTime(until) ?? { hour: 23, minute: 59, second: 59 };

        // an extra day needs to be added when the booking day crosses midnight
        // const now = DateTime.local();
        const nowTime = { hour: now.hour as number, minute: now.minute as number, second: now.second as number }
        const isNowBeforeUntil = compareTime(nowTime, untilTime) < 0; //after Midnight e.g. 03:05 is less than the configured until e.g. 17:00 .. then no need to add extra day.
        const isFromBeforeUntil = compareTime(fromTime, untilTime) < 0;
        if (!isNowBeforeUntil && !isFromBeforeUntil)
            bookingEnd = bookingEnd.plus({ days: 1 });
        return bookingEnd.set(untilTime);
    }
    else {
        return bookingStart.plus(Duration.fromObject({ hours: units }));
    }
}

export function assertAmountRounding(number: number) {
    return Number(Math.round(parseFloat(`${number}e2`)) + 'e-2')
}